import type {PayloadAction} from '@reduxjs/toolkit';
import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {persistReducer} from 'redux-persist';
import sessionStorage from 'redux-persist/lib/storage/session';
import type {SelectOption} from 'src/api/ProactApiClient';
import {projectApiClient} from 'src/api/ProactApiClientInstances';
import {emptySplitApi} from 'src/redux/api';
import type {AppDispatch} from 'src/redux/store';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';

import {purgeInstanceScopedState} from './instanceSwitchActions';

export interface ProjectScope {
  id: number;
  name: string;
}

export interface ProjectScopeState {
  scope: ProjectScope | null;
  options: SelectOption[];
  isLoading: boolean;
}

export const getProjectScopeOptions = createAsyncThunk(
  'projectScope/getOptions',
  async (_, thunkAPI) => {
    try {
      return await projectApiClient.getScopeOptions();
    } catch (error) {
      return thunkAPI.rejectWithValue(parseErrorMessage(error));
    }
  }
);

// Sets the sidebar project scope then resets every RTK cache so all lists
// refetch with the new X-Project-Id header (same pattern as setActiveInstance).
export const setProjectScopeAndReset =
  (scope: ProjectScope | null) => (dispatch: AppDispatch) => {
    dispatch(setProjectScope(scope));
    dispatch(emptySplitApi.util.resetApiState());
  };

const initialState: ProjectScopeState = {
  scope: null,
  options: [],
  isLoading: false,
};

export const projectScopeSlice = createSlice({
  name: 'projectScope',
  initialState,
  reducers: {
    setProjectScope: (state, action: PayloadAction<ProjectScope | null>) => {
      state.scope = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(purgeInstanceScopedState, () => initialState)
      .addCase(getProjectScopeOptions.pending, state => {
        state.isLoading = true;
      })
      .addCase(
        getProjectScopeOptions.fulfilled,
        (state, action: PayloadAction<SelectOption[]>) => {
          state.isLoading = false;
          state.options = action.payload;
          // Reconcile a persisted scope against the caller's current options; an
          // empty list is a valid result and clears the scope.
          if (
            state.scope &&
            !action.payload.some(o => o.id === state.scope?.id)
          ) {
            state.scope = null;
          }
        }
      )
      .addCase(getProjectScopeOptions.rejected, state => {
        state.isLoading = false;
      });
  },
});

export const {setProjectScope} = projectScopeSlice.actions;

const persistConfig = {
  key: 'projectScope',
  storage: sessionStorage,
  whitelist: ['scope'],
};

export default persistReducer(persistConfig, projectScopeSlice.reducer);
